import { reactive, getCurrentInstance, onMounted } from "vue";
import { usePositions } from "@/composables/positions/usePositions";
// import { useEmployees } from "@/composables/employees/useEmployees";
import { useFilters } from "@/composables/filters/useFilters";
import { useRoles } from "@/composables/catalogs/useRoles";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { nextTick } from "vue";
import {
  getRolesListAvailableForPosition,
  getSingleEmployeeV2,
  recalculateEmployeeReadiness,
} from "@/api";
import { transformChatterData } from "@/utils/chatterUtils";

let instance = null;

export function usePositionViewState() {
  if (instance) return instance;

  const positions = usePositions();
  const { getEmployeeAvatar } = usePositions();
  // const employees = useEmployees();
  const filter = useFilters();
  const roles = useRoles();
  const { highlight } = useHighlightRow();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const state = reactive({
    filters: {
      values: [],
      searchText: "",
      showFilterDialog: false,
      key: "organizationPositions",
      clearSelection: "",
      statusOptions: ["draft", "active", "retired", "inactive"],
      filterGroups: [
        {
          label: "Archived",
          items: [
            { label: "Do not Show", value: "no_archived" },
            { label: "Show", value: "show_archived" },
          ],
        },
      ],
    },
    archiveAction: null,
    addPositionModal: {
      show: false,
    },
    activatePositionModal: {
      show: false,
    },
    deletePositionModal: {
      show: false,
      message: "",
    },
    renamePositionModal: {
      show: false,
    },
    attachRoleModal: {
      show: false,
      roles: [],
      attachedRoles: [],
      selectedRoleId: null,
      page: 1,
      isLoadingMore: false,
      hasMoreData: true,
      currentSearchTerm: "",
      currentPositionId: null,
    },
    relieveAssignment: {
      show: false,
      currentPosition: null,
      assignedEmployee: null,
      type: "",
    },
    archivePositionModal: {
      show: false,
    },
    retirePositionModal: {
      show: false,
    },
    reactivatePositionModal: {
      show: false,
    },
    clonePositionModal: {
      show: false,
    },
    errorPopup: {
      show: false,
      title: "",
      message: "",
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalPositionChatter: 0,
      page: 1,
      positionId: null,
    },
    openDefault: false,
  });

  // Filter Operations
  const getFilteredPositions = (positions) => {
    let filteredPositions = [...positions];

    // Apply text search filter
    if (state.filters.searchText?.trim()) {
      const searchText = state.filters.searchText.toUpperCase();
      filteredPositions = filteredPositions.filter((position) => {
        return (
          position.name.toUpperCase().includes(searchText) ||
          position.code?.toString().includes(searchText)
        );
      });
    }

    // Apply status filters
    if (state.filters.values.some((val) => state.filters.statusOptions.includes(val))) {
      if (state.filters.values.includes("Show")) {
        filteredPositions = filteredPositions.filter((position) =>
          state.filters.values.includes(position.status)
        );
      } else {
        filteredPositions = filteredPositions.filter(
          (position) => state.filters.values.includes(position.status) && !position.archived
        );
      }
    }

    // Apply archived filters
    const archivedFilters = ["Show", "Do not Show"];
    if (state.filters.values.some((val) => archivedFilters.includes(val))) {
      filteredPositions = filteredPositions.filter((position) => {
        return state.filters.values.some((filter) => {
          if (filter === "Show") {
            return true; // Show all
          } else if (filter === "Do not Show") {
            return !position.archived;
          }
          return false;
        });
      });
    }

    // Default archived filter if no filters applied
    if (state.filters.values.length === 0) {
      filteredPositions = filteredPositions.filter((position) => !position.archived);
    }

    return filteredPositions.sort((a, b) => a.name.localeCompare(b.name));
  };

  const applyFilters = async (filters) => {
    const newFilters = filters
      .map((item) => {
        if (item.value !== null) {
          return typeof item === "string" ? item : item.value;
        }
        return null;
      })
      .filter(Boolean);
    state.filters.values = newFilters;
    filter.setFilters({
      key: state.filters.key,
      filters: newFilters,
    });
    positions.clearPositionDynamicFilters();
    let archivedFilterApplied = false;
    state.filters.values.forEach((el) => {
      if (el == "draft" || el == "active" || el == "retired" || el == "inactive") {
        positions.addPositionFilter("status", el);
      } else if (el == "Show") {
        archivedFilterApplied = true;
        positions.addPositionFilter("archived", true);
      } else if (el == "Do not Show") {
        archivedFilterApplied = true;
        positions.addPositionFilter("archived", false);
      }
    });
    if (!archivedFilterApplied) {
      positions.addPositionFilter("archived", false);
    }
    state.filters.showFilterDialog = false;
    positions.setPaginationDataOffset(0);
    await nextTick();
    positions.loadAllPositions();
  };

  const handleRequest = async ({ page, itemsPerPage }) => {
    positions.setPaginationDataLimit(itemsPerPage);
    positions.setPaginationDataOffset((page - 1) * itemsPerPage);
    await positions.loadAllPositions();
  };

  const handleSort = async (sortData) => {
    const finalSortField = sortData.descending ? `-${sortData.field}` : `${sortData.field}`;
    positions.setOrderingDataField(finalSortField);
    await positions.loadAllPositions();
  };

  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string) => {
    // Always clear existing search filters first
    positions.removePositionFilter("name");

    // Only add search filters and load positions if there's actually a search term
    if (string && string.trim() !== "") {
      positions.addPositionFilter("name", string);
    }
    positions.setPaginationDataOffset(0);
    state.filters.searchText = string;
    await positions.loadAllPositions();
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string) => {
    const queryString = string;
    debouncedSearch(queryString);
  };

  const removeFilters = (removedFilter) => {
    positions.clearPositionDynamicFilters();
    positions.setPaginationDataOffset(0);
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilter;
    applyFilters(state.filters.values.map((value) => ({ label: value, value: value })));
  };

  const clearFilters = async () => {
    // ER-11195: "Clear all" restores the documented defaults (Draft/Active,
    // archived hidden) from the central registry instead of an unfiltered list.
    state.filters.values = filter.resetToDefaults(state.filters.key);
    await applyFilters(state.filters.values);
  };

  const handlePositionOperation = async (operation, accountId, payload = null) => {
    try {
      proxy.$loader.setAppLoading(true);
      let newPosition;
      let clonedPosition;

      switch (operation) {
        case positions.operations.CREATE_POSITION:
          if (!payload) return;
          newPosition = await positions.createPosition({
            accountId,
            name: payload,
          });
          state.addPositionModal.show = false;
          positions.clearPositionDynamicFilters();
          state.filters.values = [];
          // await positions.loadAllPositions();
          state.filters.searchText = newPosition.code_str;
          await performSearch(newPosition.code_str);
          setTimeout(() => {
            highlight({ id: newPosition.id, type: "Positions" });
          }, 1000);
          break;

        case positions.operations.ACTIVATE_POSITION:
          await positions.activate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.activatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.DELETE_POSITION: {
          const positionToDelete = positions.getCurrentPosition();
          const positionName = (positionToDelete?.name || "").trim();
          await positions.deletePosition({
            accountId,
            position: positionToDelete,
          });
          state.deletePositionModal.show = false;
          proxy?.$notifications?.success?.(
            "Position successfully deleted",
            positionName
              ? `Position ${positionName} was successfully deleted`
              : "The position was successfully deleted."
          );
          await positions.loadAllPositions();
          break;
        }

        case positions.operations.RENAME_POSITION:
          if (!payload) return;
          await positions.rename({
            accountId,
            position: positions.getCurrentPosition(),
            newName: payload,
          });
          state.renamePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.ARCHIVE_POSITION:
          if (state.archiveAction === "archive") {
            await positions.archive({
              accountId,
              position: positions.getCurrentPosition(),
            });
          } else if (state.archiveAction === "unarchive") {
            await positions.unarchive({
              accountId,
              position: positions.getCurrentPosition(),
            });
          }
          state.archivePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.RETIRE_POSITION:
          await positions.retire({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.retirePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.REACTIVATE_POSITION:
          await positions.reactivate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.reactivatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.CLONE_POSITION: {
          clonedPosition = await positions.clonePosition({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.clonePositionModal.show = false;
          positions.clearPositionDynamicFilters();
          state.filters.values = [];
          state.filters.searchText = clonedPosition.code_str;
          await performSearch(clonedPosition.code_str);
          setTimeout(() => {
            highlight({ id: clonedPosition.id, type: "Positions" });
          }, 1000);
          await roles.loadAllRoles();
          break;
        }
      }
      positions.setCurrentPosition(null);
    } catch (error) {
      console.error("Error in position operation:", error);
      if (error.status === "400") {
        state.errorPopup.title = "Operation Failed";
        state.errorPopup.message = error.response?.data.join(" ") || "Operation failed";
        state.errorPopup.show = true;
      }
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  };

  const handlePositionDropdownOperation = async (operation, position) => {
    if (!operation || !position) return;

    positions.setCurrentPosition(position);

    switch (operation.value) {
      case "activate_action":
        state.activatePositionModal.show = true;
        break;
      case "delete_action":
        state.deletePositionModal.show = true;
        break;
      case "rename_action":
        state.renamePositionModal.show = true;
        break;
      case "attach_position_role":
        await prepareAttachRole();
        state.openDefault = true;
        break;
      case "archive_action":
        state.archiveAction = "archive";
        state.archivePositionModal.show = true;
        break;
      case "unarchive_action":
        state.archiveAction = "unarchive";
        state.archivePositionModal.show = true;
        break;
      case "retire_action":
        state.retirePositionModal.show = true;
        break;
      case "reactivate_action":
        state.reactivatePositionModal.show = true;
        break;
      case "cloned_action":
        state.clonePositionModal.show = true;
        break;
    }
  };

  // UI Helpers
  const getPositionCode = (position) => {
    if (position?.code_str) {
      const match = position.code_str.match(/#\d+/);
      return match ? match[0] : "";
    }
    return "#";
  };

  const getStatusColor = (status) => {
    return status === "active" ? "green" : "gray";
  };

  const getPrimaryPositionOccupationProps = (position) => {
    const baseProps = {
      employee1name: "",
      employee1picture: "",
      employee2name: "",
      employee2picture: "",
      hasPrimaryOccupation: false,
      hasVacant: false,
      label: "",
      type: "",
      rolesCount: 0,
      responsibilityCount: 0,
      positionCode: "",
      positionName: "",
      addHoverEffect: true,
      effective_date: "",
    };

    if (!position || position.status !== "active") {
      return { ...baseProps, label: "Not allowed" };
    }

    const vacantCapacity = position?.vacancy || 0;
    if (vacantCapacity > 0) {
      if (position?.assigned_primary || position?.assigned_primary == null) {
        return {
          ...baseProps,
          hasVacant: true,
          label: "Occupy",
        };
      }
    } else {
      const primaryEmployee = position?.assigned_primary;
      const preparingEmployee = position?.assigned_preparing_for;
      baseProps.hasPrimaryOccupation = true;
      baseProps.hasVacant = false;
      baseProps.label = "";
      baseProps.employee1name = primaryEmployee?.assigned_employee_name || "";
      baseProps.employee2name = preparingEmployee?.assigned_employee_name || "";
      baseProps.employee1picture = getEmployeeAvatar(primaryEmployee);
      baseProps.employee2picture = getEmployeeAvatar(preparingEmployee);
      baseProps.rolesCount = position?.roles_count || 0;
      baseProps.responsibilityCount = position?.responsibilities_count || 0;
      baseProps.positionCode = position?.code || null;
      baseProps.positionName = position?.name;
      baseProps.effective_date = position?.primary_position_effective_on || null;
      baseProps.fteText = position?.capacity || null;
      const obj = {
        ...baseProps,
        hasPrimaryOccupation: true,
        hasVacant: false, // An occupied position is not vacant in this context
        label: "",
        employeeId: primaryEmployee?.id || null,
      };
      return obj;
    }

    if (vacantCapacity > 0 || (vacantCapacity === 0 && !position.assigned_employees)) {
      return {
        ...baseProps,
        hasVacant: true,
        label: "Occupy",
      };
    }
    return { ...baseProps, label: "State Undefined" };
  };

  const prepareAttachRole = async () => {
    const currentPosition = positions.getCurrentPosition();
    if (!currentPosition?.id) return;
    state.attachRoleModal.currentPositionId = currentPosition.id;
    await getListOfRoles(currentPosition.id, 1, "", false);
    state.attachRoleModal.show = true;
  };
  const getListOfRoles = async (positionId, page = 1, searchTerm = "", isLoadMore = false) => {
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    try {
      // Set loading state for pagination
      if (isLoadMore) {
        state.attachRoleModal.isLoadingMore = true;
      }

      const attachableeRoles = await getRolesListAvailableForPosition(
        accountId,
        positionId,
        limit,
        offset,
        name
      );

      const newItems = attachableeRoles.data.results.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/)[0],
        name: item.name,
        status: item.status,
        attached: item?.attached_to_position,
      }));

      // If it's a load more request, append to existing items
      if (isLoadMore && state.attachRoleModal.roles) {
        state.attachRoleModal.roles = [...state.attachRoleModal.roles, ...newItems];
      } else {
        // If it's initial load or search, replace items
        state.attachRoleModal.roles = newItems;
      }

      // Update pagination state
      state.attachRoleModal.page = page;
      state.attachRoleModal.totalCount = attachableeRoles.data.count || 0;
      // hasMoreData should be true if there are more records beyond current page
      state.attachRoleModal.hasMoreData = page * limit < state.attachRoleModal.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      // Clear loading state
      if (isLoadMore) {
        state.attachRoleModal.isLoadingMore = false;
      }
    }
  };

  // Method to handle load more for pagination
  const loadMoreRoles = async () => {
    if (state.attachRoleModal.isLoadingMore || !state.attachRoleModal.hasMoreData) {
      return;
    }

    const nextPage = state.attachRoleModal.page + 1;
    await getListOfRoles(
      state.attachRoleModal.currentPositionId,
      nextPage,
      state.attachRoleModal.currentSearchTerm,
      true
    );
  };

  // Method to handle search with pagination reset
  const searchRoles = async (searchTerm) => {
    state.attachRoleModal.currentSearchTerm = searchTerm;
    state.attachRoleModal.page = 1;
    state.attachRoleModal.hasMoreData = true;
    await getListOfRoles(state.attachRoleModal.currentPositionId, 1, searchTerm, false);
  };
  const handleRoleSelected = (role) => {
    if (role?.id) {
      state.attachRoleModal.selectedRoleId = role.id;
    }
  };

  const handleAddRoleSelected = async (data) => {
    proxy.$loader.show();
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const newRole = await roles.saveNewRole({
        accountId: accountId,
        roleName: data.name,
      });
      if (newRole) {
        state.attachRoleModal.selectedRoleId = newRole.id;
      }
      await attachRole(accountId);
    } finally {
      proxy.$loader.hide();
    }
  };

  const attachRole = async (accountId) => {
    if (!state.attachRoleModal.selectedRoleId || !positions.getCurrentPosition()?.id) return;
    try {
      proxy.$loader.show();
      await positions.attachRole({
        accountId,
        positionId: positions.getCurrentPosition().id,
        roleId: state.attachRoleModal.selectedRoleId,
      });
      state.attachRoleModal.show = false;
      await positions.loadAllPositions();
      await roles.loadAllRoles();
    } catch (e) {
      console.error(e);
    } finally {
      proxy.$loader.hide();
    }
  };

  // Assignment Management
  const onRelievePrimaryAssignment = (position) => {
    if (position.assigned_employees?.length > 0) {
      state.relieveAssignment.show = true;
      state.relieveAssignment.type = "primary";
      state.relieveAssignment.assignedEmployee = position.assigned_primary;
      state.relieveAssignment.currentPosition = position;
    }
  };
  const onRelievePrepareAssignment = (position) => {
    let assignedEmployee = null;
    if (Array.isArray(position?.assigned_preparing_for)) {
      assignedEmployee = position?.assigned_preparing_for[0];
    } else {
      // is object
      assignedEmployee = position?.assigned_preparing_for;
    }
    if (position.assigned_employees?.length > 0) {
      state.relieveAssignment.show = true;
      state.relieveAssignment.type = "prepare";
      state.relieveAssignment.assignedEmployee = assignedEmployee;
      state.relieveAssignment.currentPosition = position;
    }
  };

  const closeRelieveAssignment = () => {
    state.relieveAssignment.show = false;
    state.relieveAssignment.currentPosition = null;
    state.relieveAssignment.assignedEmployee = null;
  };

  const getRelieveAssignmentAvatar = () => {
    const employee = state.relieveAssignment.assignedEmployee;
    return employee
      ? {
          picture: getEmployeeAvatar(employee),
          firstName: `${employee.assigned_employee_name.split(" ")[0] || "U"}`,
          lastName: `${employee.assigned_employee_name.split(" ")[1] || "S"}`,
        }
      : null;
  };

  const relievePrimaryAssignment = async (accountId, effectiveTo) => {
    const position = state.relieveAssignment.currentPosition;
    if (state.relieveAssignment.type == "primary") {
      if (!position?.assigned_primary) return;
      proxy.$loader.setAppLoading(true);
      try {
        const idEmployee = position.assigned_primary.assigned_employee_id || null;
        const employeeData = await getSingleEmployeeV2(accountId, idEmployee);
        const assignmentId = employeeData?.data?.primary_position?.id || null;
        const employeeId = employeeData?.data?.id || null;

        await positions.relievePrimaryAssignment({
          accountId,
          assignmentId,
          effectiveTo,
        });

        await positions.loadAllPositions();
        await recalculateEmployeeReadiness(accountId, employeeId);
        closeRelieveAssignment();
      } catch (error) {
        console.error("Error relieving primary assignment:", error);
      } finally {
        proxy.$loader.setAppLoading(false);
      }
    } else if (state.relieveAssignment.type == "prepare") {
      if (!position.assigned_preparing_for) return;
      proxy.$loader.setAppLoading(true);
      try {
        const idEmployee = state.relieveAssignment?.assignedEmployee?.assigned_employee_id || null;
        const employeeData = await getSingleEmployeeV2(accountId, idEmployee);
        const assignedPosition = employeeData?.data?.preparing_for_positions.find(
          (el) => el.code_str == position.code_str
        );
        const assignmentId = assignedPosition.id;
        const employeeId = employeeData?.data?.id || null;
        await positions.relievePrimaryAssignment({
          accountId,
          assignmentId,
          employeeId,
          effectiveTo,
        });

        await positions.loadAllPositions();
        await recalculateEmployeeReadiness(accountId, employeeId);
        closeRelieveAssignment();
      } catch (error) {
        console.error("Error relieving primary assignment:", error);
      } finally {
        proxy.$loader.setAppLoading(false);
      }
    }
  };

  // Capacity Management
  const handleCapacityChange = async (capacity, position, accountId) => {
    proxy.$loader.setAppLoading(true);
    try {
      await positions.updateCapacity({
        accountId,
        positionId: position.id,
        capacity,
        name: position.name,
      });
      await positions.loadAllPositions();
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  };

  const openPositionDetails = async (position) => {
    if (!proxy.$loader.isAppLoading) {
      await proxy.$router.push(`/organization/positions/position/${position.id}`);
    }
  };

  const getFilters = () => {
    const persistedFilters = filter.getFilters(state.filters.key);
    if (persistedFilters) {
      state.filters.values = persistedFilters;
    }
  };

  const clearSearch = async () => {
    state.filters.searchText = "";
    // Clear search filters from dynamic filters
    positions.removePositionFilter("name");
  };
  const getPositionChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await positions.positionChatter(
        accountID,
        state.openChatterModal.positionId,
        "position",
        state.openChatterModal.page
      );
      if (response) {
        const chatterItems = transformChatterData(response.data.results);
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalPositionChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };
  const openPositionChatterModal = async (item) => {
    positions.setCurrentPosition(item);
    state.openChatterModal.chatterData = [];
    state.openChatterModal.positionId = item.id;
    await getPositionChatter();
  };
  const closePositionChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalPositionChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };
  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getPositionChatter();
  };
  const getAvatarPeopleProps = (item) => {
    const preparingForEmployees = item.assigned_employees
      .filter((emp) => emp.commitment === "Preparing for")
      .map((emp) => ({
        firstName: emp.first_name,
        lastName: emp.last_name,
        img: emp.avatar,
        fullName: `${emp.first_name} ${emp.last_name}`,
      }));
    return {
      avatars: preparingForEmployees,
      maxVisible: 3,
      isShowTooltip: true,
      size: "medium",
    };
  };

  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    // Check feature flag for hiding archived filter
    const hideArchivedFilterPositions = await proxy.$getFlag(
      "hide_archived_filter_positions",
      false
    );

    if (hideArchivedFilterPositions) {
      // Remove archived filter from filterGroups
      state.filters.filterGroups = state.filters.filterGroups.filter(
        (group) => group.label !== "Archived"
      );
    }
  };

  onMounted(async () => {
    await initializeFilters();
    getFilters();
    // applyFilters(
    //   state.filters.values.map((value) => ({ label: value, value: value }))
    // );
  });

  instance = {
    state,
    getFilteredPositions,
    applyFilters,
    removeFilters,
    handlePositionOperation,
    handlePositionDropdownOperation,
    getPositionCode,
    getStatusColor,
    getPrimaryPositionOccupationProps,
    handleRoleSelected,
    handleAddRoleSelected,
    attachRole,
    onRelievePrimaryAssignment,
    onRelievePrepareAssignment,
    closeRelieveAssignment,
    getRelieveAssignmentAvatar,
    relievePrimaryAssignment,
    handleCapacityChange,
    openPositionDetails,
    clearSearch,
    openPositionChatterModal,
    closePositionChatterModal,
    requestMoreChatter,
    getAvatarPeopleProps,
    handleSort,
    handleSearch,
    handleRequest,
    loadMoreRoles,
    searchRoles,
    getListOfRoles,
    clearFilters,
    initializeFilters,
  };

  return instance;
}

export function resetPositionViewState() {
  instance = null;
}
